import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Measures the active tab so a single indicator can slide between tabs.
 *
 * The list element must be `relative`: offsetLeft is read against it, which
 * keeps the measure right inside a horizontally scrolled bar. `indicator` is
 * null until a measure succeeds (or when the bar is hidden), and the caller
 * then keeps its static underline.
 *
 * `tabKeys` lists the tabs in order, so adding or removing one re-measures.
 */
const useActiveTabIndicator = (activeKey, tabKeys = []) => {
  // A callback ref kept in state: a bar mounted after the first render (once
  // its data has loaded) or remounted must still be measured and observed.
  const [listNode, listRef] = useState(null);
  const tabRefs = useRef(new Map());
  const [indicator, setIndicator] = useState(null);

  const registerTab = useCallback(
    (key) => (node) => {
      if (node) tabRefs.current.set(key, node);
      else tabRefs.current.delete(key);
    },
    [],
  );

  const measure = useCallback(() => {
    const tab = tabRefs.current.get(activeKey);
    if (!tab || !tab.offsetWidth) {
      setIndicator(null);
      return;
    }
    const next = {
      left: tab.offsetLeft,
      top: tab.offsetTop,
      width: tab.offsetWidth,
      height: tab.offsetHeight,
    };
    setIndicator((prev) =>
      prev &&
      prev.left === next.left &&
      prev.top === next.top &&
      prev.width === next.width &&
      prev.height === next.height
        ? prev
        : next,
    );
  }, [activeKey]);

  const tabKeysSignature = tabKeys.join('\u0000');

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined' || !listNode) return undefined;
    // Each tab is observed too: a label or a badge that changes width moves
    // the tabs after it without resizing the list.
    const observer = new ResizeObserver(measure);
    observer.observe(listNode);
    tabRefs.current.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [measure, tabKeysSignature, listNode]);

  return { listRef, registerTab, indicator };
};

export default useActiveTabIndicator;
